import {Component, ElementRef, Inject, LOCALE_ID, Renderer2, ViewChild} from '@angular/core';
import {BaseComponent} from "../../../../base/components/base.component";
import {EventBusService} from "../../../../base/services/eventbus/event-bus.service";
import {ActivatedRoute, Router, UrlTree} from "@angular/router";
import {SubscriptionContainerService} from "../../../../base/services/subscription-container.service";
import {AuthService} from "../../../common/modules/auth/services/auth.service";
import {TokenPayload} from "../../../common/modules/auth/models/auth.model";
import {DialogService} from "../../../common/modules/utility/components/popup/dialog.service";
import {LeaderboardComponent} from "../../../common/modules/accounting/leaderboard/leaderboard.component";
import {FormControl} from "@angular/forms";
import {Locales, MenuTypeKey, MenuTypes, Theme, ThemeKey} from "../../../../models/global.config";
import {LocalStorageService} from "../../../../base/services/storage/localstorage.service";
import {Utility} from "../../../common/modules/utility/utility";
import {EventKey} from "../../../../registry/event.key";
import {FullScreenService} from "../../services/full-screen.service";
import {environment} from "../../../../../environments/environment";
import {setTheme} from "ngx-bootstrap/utils";

interface OrgDisplay {
  alias: string
  name: string
}

@Component({
  selector: 'app-dash-header',
  templateUrl: './dash-header.component.html',
  styleUrls: ['./dash-header.component.scss']
})
export class DashHeaderComponent extends BaseComponent {
  @ViewChild('rightBarToggle') rightBarToggle: ElementRef;

  protected readonly Locals = Locales;

  search = new FormControl()

  auth: TokenPayload
  isAdmin: boolean = false

  authorities: string[] = []
  orgs: OrgDisplay[] = []
  selectedOrg: OrgDisplay | null = null

  currentLocale: Locales = Locales.EN
  menuType: MenuTypes = MenuTypes.VERTICAL
  currentTheme: Theme = Theme.LIGHT

  protected componentName(): string {
    return DashHeaderComponent.name;
  }

  constructor(
    eventBus: EventBusService,
    router: Router,
    subContainer: SubscriptionContainerService,
    private authService: AuthService,
    private renderer: Renderer2,
    private popupService: DialogService<null>,
    private activatedRoute: ActivatedRoute,
    @Inject(LOCALE_ID) protected locale: string,
    private fullScreenService: FullScreenService
  ) {
    super(eventBus, router, subContainer)
    this.auth = this.authService.getPayload()
    this.isAdmin = this.authService.isAdmin()
    this.authorities = authService.getAuthorities()

    this.fullScreenService.initFullScreen();

    LocalStorageService.retrieveAsync("locale", (lc) => {
      this.currentLocale = this.normalizeLocale(lc)
    })

    LocalStorageService.retrieveAsync(ThemeKey.DASHBOARD, (theme) => {
      this.currentTheme = (theme as Theme) || Theme.LIGHT
    })
  }

  logoUrl(wide: boolean = false): string {
    return wide ? environment.logo_wide_uri : environment.logo_uri
  }

  hasPrivilege(privilege: string): boolean {
    return this.isAdmin || this.authorities.includes(privilege)
  }

  changePassword() {
    window.open(environment.realm_url + "/account/account-security/signing-in", "_blank")
  }

  ngOnInit(): void {
    this.activatedRoute.queryParams.subscribe(params => {
      this.search.setValue(params['query'])
    })

    LocalStorageService.retrieveAsync("locale", (lc) => {
      this.currentLocale = this.normalizeLocale(lc)
    })

    this.resolveOrganizations()
  }

  private resolveOrganizations() {
    const orgAliases = this.authService.getOrganizationsFromToken();
    this.orgs = orgAliases.map(alias => this.aliasToOrgDisplay(alias));

    LocalStorageService.retrieveAsync("selectedOrgAlias", (selectedAlias) => {
      if (selectedAlias && orgAliases.includes(selectedAlias)) {
        this.selectedOrg = this.aliasToOrgDisplay(selectedAlias);
      } else if (this.orgs.length > 0) {
        this.onOrgSelected(this.orgs[0]);
      }
    })
  }

  onOrgSelected(org: OrgDisplay) {
    this.selectedOrg = org
    LocalStorageService.put("selectedOrgAlias", org.alias)
    this.reloadRouteByUrl()
  }

  private aliasToOrgDisplay(alias: string): OrgDisplay {
    // Convert alias like "sayemdev-local" to "Sayemdev Local"
    const name = alias
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    return { alias, name };
  }

  onSearch() {
    const urlTree: UrlTree = this.router.parseUrl(this.router.url);
    const path = urlTree.root.children['primary'].segments.map(segment => segment.path).join('/');

    this.router.navigate([path], {
      queryParams: {
        query: this.search.value,
      },
      queryParamsHandling: 'merge',
    }).then(r => {
    });
  }

  clearSearch() {
    this.search.setValue(null)
    this.onSearch()
  }

  logout() {
    this.authService.logout()
  }

  initLeftMenuCollapse() {
    const body = document.body;

    body.classList.toggle('sidebar-enable');
    if (window.innerWidth >= 992) {
      body.classList.toggle('vertical-collpsed');
    } else {
      body.classList.remove('vertical-collpsed');
    }

    if (body.classList.contains('vertical-collpsed')) {
      this.renderer.addClass(body, 'vertical-collpsed');
    } else {
      this.renderer.removeClass(body, 'vertical-collpsed');
    }
  }

  initRightSidebar() {
    // right side-bar toggle
    const body = document.querySelector('body');

    this.renderer.listen(body, 'click', (e: Event) => {
      const target = e.target as HTMLElement;

      if (target.closest('.right-bar-toggle, .right-bar')) {
        return;
      }

      this.renderer.removeClass(body, 'right-bar-enabled');
    });

    this.renderer.listen(document.querySelector('.right-bar-toggle'), 'click', () => {
      this.renderer.addClass(body, 'right-bar-enabled');
    });
  }

  openLeaderboard() {
    let popup = this.popupService.open(LeaderboardComponent, {id: null})
    let component = popup.componentInstance
    component.onCanceled.subscribe(() => {
      popup.close()
    })
  }

  setLocale(locale: Locales): void {
    LocalStorageService.put('locale', locale)

    this.currentLocale = locale;
    const currentUrl = this.router.url;
    let newUrl = currentUrl.replace(/\/[a-z]{2}(?:-[A-Z]{2})?\//, `/${locale}/`);
    if (!newUrl.startsWith('/' + locale + '/')) {
      newUrl = '/' + locale + '/' + newUrl;
    }
    window.location.href = window.location.origin + newUrl;
  }

  onMenuTypeSelected(menuType: MenuTypes) {
    LocalStorageService.put(MenuTypeKey.DASHBOARD, menuType)
    this.menuType = menuType;
    window.location.reload()
  }

  private normalizeLocale(locale: unknown): Locales {
    if (locale === 'en-US') return Locales.EN;
    if (locale === 'bn-BN') return Locales.BN_BN;
    return (locale as Locales) || Locales.EN;
  }

  onMenuThemeSelected(theme: Theme) {
    LocalStorageService.put(ThemeKey.DASHBOARD, theme)
    this.currentTheme = theme;
    console.log("Current Theme", this.currentTheme)
    window.location.reload()
    // setTimeout(() => {
    // }, 1000)
  }

  toggleFullScreen(event: Event): void {
    event.preventDefault();
    document.body.classList.toggle('fullscreen-enable');
    this.fullScreenService.toggleFullScreen();
  }

  protected readonly Utility = Utility;

  protected readonly Locales = Locales;

  protected readonly MenuTypes = MenuTypes;
  protected readonly setTheme = setTheme;
  protected readonly Theme = Theme;
}
